/** @jsxImportSource @emotion/react */
import { Snackbar as MuiSnackbar, Alert as MuiAlert } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { createStyles, Typography, type IconName } from '../..';
import { Icon } from '../Icon';

type Variant = 'primary' | 'warning' | 'danger' | 'success';

interface Props {
  message: ReactNode | ReactNode[];
  variant: Variant;
  onClose?: () => void;
  icon?: IconName;
  showIcon?: boolean;
  open?: boolean;
  position?: {
    vertical: 'bottom' | 'top';
    horizontal: 'left' | 'right' | 'center';
  };
  showAsBanner?: boolean;
  hideDuration?: number | null;
  className?: string;
}

const muiVariantMap: Record<Variant, 'success' | 'error' | 'warning' | 'info'> =
  {
    primary: 'info',
    warning: 'warning',
    success: 'success',
    danger: 'error',
  };

/**
 *
 * @param showIcon
 * @returns state to show icon in snackbar - set it to false to not show the icon
 */

export const Snackbar = ({
  variant,
  message,
  onClose,
  icon,
  showIcon = true,
  open = false,
  showAsBanner = false,
  position = { vertical: 'top', horizontal: 'right' },
  hideDuration = 7000,
  className,
}: Props) => {
  const styles = useStyles();
  const [show, setShow] = useState(open);

  const handleClose = () => {
    if (onClose) onClose();
    setShow(false);
  };

  useEffect(() => {
    if (open) setShow(true);
  }, [open]);

  if (showAsBanner)
    return (
      <MuiAlert
        className={className}
        css={[styles.default, styles[variant]]}
        severity={muiVariantMap[variant]}
        icon={showIcon && icon && <Icon name={icon} />}
      >
        {typeof message === 'string' ? (
          <Typography variant="bodySmall">{message}</Typography>
        ) : (
          message
        )}
      </MuiAlert>
    );

  return (
    <MuiSnackbar
      open={show}
      autoHideDuration={hideDuration}
      anchorOrigin={position}
      onClose={handleClose}
      css={styles.safeArea}
    >
      <MuiAlert
        css={[styles.default, styles[variant]]}
        severity={muiVariantMap[variant]}
        icon={showIcon && icon && <Icon name={icon} />}
        onClose={handleClose}
      >
        {Array.isArray(message) ? (
          <ul css={styles.messageList}>
            {message.map((msg) => (
              <li key={msg?.toString()}>
                {typeof msg === 'string' ? (
                  <Typography variant="bodySmall">{msg}</Typography>
                ) : (
                  msg
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div css={styles.alertMessage}>
            {typeof message === 'string' ? (
              <Typography variant="bodySmall">{message}</Typography>
            ) : (
              message
            )}
          </div>
        )}
      </MuiAlert>
    </MuiSnackbar>
  );
};

const useStyles = createStyles(({ palette, spacing }) => ({
  safeArea: {
    padding:
      'var(--safe-area-inset-top, 0) var(--safe-area-inset-right, 0) var(--safe-area-inset-bottom, 0) var(--safe-area-inset-left, 0)',
  },
  default: {
    padding: `${spacing.xxxs} ${spacing.xxs}`,
    width: '100%',
    boxShadow: `0px 4px 8px ${palette.black}0f`,
    '.MuiAlert-icon': {
      alignItems: 'center',
    },
    '.MuiAlert-action': {
      padding: 0,
      paddingTop: `calc(${spacing.xxxs}/2)`,
      position: 'relative',
      right: 0,
    },
  },
  success: {
    '&.MuiAlert-standardSuccess': {
      background: palette.success.x50,
      color: palette.success.x700,
      border: `1px solid ${palette.success.x200}`,

      '.MuiAlert-icon': {
        color: palette.success.x700,
      },
    },
  },
  primary: {
    '&.MuiAlert-standardInfo': {
      background: palette.primary.x50,
      color: palette.primary.x700,
      border: `1px solid ${palette.primary.x200}`,

      '.MuiAlert-icon': {
        color: palette.primary.x700,
      },
    },
  },
  warning: {
    '&.MuiAlert-standardWarning': {
      background: palette.warning.x50,
      color: palette.warning.x700,
      border: `1px solid ${palette.warning.x200}`,

      '.MuiAlert-icon': {
        color: palette.warning.x700,
      },
    },
  },
  danger: {
    '&.MuiAlert-standardError': {
      background: palette.danger.x50,
      color: palette.danger.x700,
      border: `1px solid ${palette.danger.x200}`,

      '.MuiAlert-icon': {
        color: palette.danger.x700,
      },
    },
  },
  alertMessage: {
    maxWidth: spacing.xxxl,
  },
  messageList: {
    listStyle: 'none',
    paddingLeft: 0,
    margin: 0,
    '> * + *': {
      marginTop: spacing.xxxs,
    },
  },
}));
